/**
 * Track Header Column (RFC-0633 round-4)
 *
 * A draggable-width column rendered to the left of the scrollable timeline
 * body. Each cell represents one sub-track (video or audio) and renders
 * the track label on row 1 (full width, no truncation pressure) and the
 * mute + remove buttons on row 2.
 *
 * Width is controlled by the parent via `columnPx`; the parent owns the
 * state + localStorage persistence. This component renders a 4px-wide
 * vertical splitter at `right:0` that fires `onColumnWidthChange` on
 * every `pointermove` so the parent can render the drag live.
 *
 * Issue 5: Each cell (except Main track) renders a small Remove (X)
 * button.
 *
 * Issue 6: Video track index 0 is special-cased — label is "Main track"
 * and the Remove button is suppressed.
 */

import { type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Volume2, VolumeX, X } from 'lucide-react'
import { createNadDebug } from '../../../../utils/debugLog'
import type { TimelineAudioTrackLabelKey } from './timelineAudioKind'

/** RFC-0633 (Issue 3): default width when no localStorage value is set. */
export const TRACK_HEADER_COLUMN_DEFAULT_WIDTH = 120
export const TRACK_HEADER_COLUMN_MIN = 60
export const TRACK_HEADER_COLUMN_MAX = 240
/**
 * Legacy export kept so callers that still import `TRACK_HEADER_COLUMN_WIDTH`
 * fall back to the new default instead of a hard-coded 80.
 */
export const TRACK_HEADER_COLUMN_WIDTH = TRACK_HEADER_COLUMN_DEFAULT_WIDTH

const layoutLog = createNadDebug('dw:video-editor:layout')

interface TrackCell {
  kind: 'video' | 'audio'
  trackIndex: number
  labelKey?: 'mainTrackWithOriginalAudio' | TimelineAudioTrackLabelKey
  /** Number of clips on this sub-track — drives the confirm prompt. */
  clipCount: number
  /** Some projection rows are visible but cannot be mutated as tracks. */
  removable?: boolean
  muteable?: boolean
  testIdSuffix?: string
}

interface TrackHeaderColumnProps {
  leadingHeader?: ReactNode
  /** Ordered list of (video sub-track, audio sub-track) cells from top to bottom. */
  cells: TrackCell[]
  /** Per-row height in pixels, indexed parallel to `cells`. */
  cellHeights: number[]
  /** Ruler offset (filler at the top so cells line up with track rows). */
  rulerHeight: number
  /** Muted track sets. */
  mutedVideoTracks?: Set<number>
  mutedAudioTracks?: Set<number>
  /** Mute toggle. */
  onToggleTrackMute?: (kind: 'video' | 'audio', trackIndex: number) => void
  /**
   * Remove a sub-track. Called only after the parent confirms with the user
   * (when the sub-track has clips). `deleteClips` mirrors the confirm
   * outcome: true when the user accepted clip deletion, false when the
   * track was empty and no confirm was needed.
   */
  onRemoveTrack?: (kind: 'video' | 'audio', trackIndex: number, deleteClips: boolean) => void
  /** Disable interactive controls (read-only timeline). */
  locked?: boolean
  /**
   * RFC-0633 (Issue 3): current column width in px. Parent owns this state
   * and the localStorage persistence. Defaults to
   * `TRACK_HEADER_COLUMN_DEFAULT_WIDTH` when omitted.
   */
  columnPx?: number
  /** RFC-0633 (Issue 3): live width-change callback fired on every pointer move. */
  onColumnWidthChange?: (nextPx: number) => void
  /** RFC-0633 (Issue 3): commit callback fired once on pointerup. */
  onColumnWidthCommit?: (nextPx: number) => void
}

export function TrackHeaderColumn({
  leadingHeader,
  cells,
  cellHeights,
  rulerHeight,
  mutedVideoTracks,
  mutedAudioTracks,
  onToggleTrackMute,
  onRemoveTrack,
  locked = false,
  columnPx = TRACK_HEADER_COLUMN_DEFAULT_WIDTH,
  onColumnWidthChange,
  onColumnWidthCommit,
}: TrackHeaderColumnProps) {
  const { t } = useTranslation('canvas')
  const loggedMainRef = useRef(false)
  const dragStartRef = useRef<{ startX: number; startWidth: number } | null>(null)

  // Issue 3: emit a `track-header-pinned` log on every width change so NAD
  // traces can confirm the user-set width is applied.
  useEffect(() => {
    layoutLog({
      event: 'track-header-pinned',
      leftColumnPx: columnPx,
      cellCount: cells.length,
    })
  }, [cells.length, columnPx])

  // Issue 6: emit a one-shot log confirming the Main track label / suppress
  // for sub-track 0.
  useEffect(() => {
    if (loggedMainRef.current) return
    const hasMain = cells.some((c) => c.kind === 'video' && c.trackIndex === 0)
    if (!hasMain) return
    loggedMainRef.current = true
    layoutLog({
      event: 'main-track-labeled',
      trackIndex: 0,
      // i18n-exempt: NAD diagnostic log value, not user-facing UI (the on-canvas
      // label is rendered via t('trackHeaderColumn.mainTrack')).
      label: 'Main track',
      removeButtonHidden: true,
    })
  }, [cells])

  const onSplitterPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (locked) return
    e.stopPropagation()
    e.preventDefault()
    const target = e.currentTarget
    try {
      target.setPointerCapture(e.pointerId)
    } catch {
      // ignore pointer-capture failures (e.g. cross-origin frames)
    }
    dragStartRef.current = { startX: e.clientX, startWidth: columnPx }
  }

  const onSplitterPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return
    const dx = e.clientX - dragStartRef.current.startX
    const next = Math.max(
      TRACK_HEADER_COLUMN_MIN,
      Math.min(TRACK_HEADER_COLUMN_MAX, dragStartRef.current.startWidth + dx),
    )
    if (next !== columnPx) {
      onColumnWidthChange?.(next)
    }
  }

  const onSplitterPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return
    const dx = e.clientX - dragStartRef.current.startX
    const next = Math.max(
      TRACK_HEADER_COLUMN_MIN,
      Math.min(TRACK_HEADER_COLUMN_MAX, dragStartRef.current.startWidth + dx),
    )
    const from = dragStartRef.current.startWidth
    dragStartRef.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // ignore
    }
    onColumnWidthCommit?.(next)
    layoutLog({ event: 'header-column-resized', from, to: next })
  }

  return (
    <div
      className="relative flex shrink-0 flex-col bg-card border-r border-border"
      style={{ width: `${columnPx}px` }}
      data-testid="timeline-track-header-column"
      data-column-px={columnPx}
    >
      {/* Ruler-height spacer so the cells align with their tracks.
          RFC-0634 (header-track scroll sync): vertical scroll now lives on
          the outer wrapper; this spacer must stay pinned at the top of the
          header column so labels do not slide under the body ruler during
          vertical scroll. `shrink-0` prevents flex from squishing it. */}
      <div
        className="sticky top-0 z-10 shrink-0 border-b border-border bg-card"
        style={{ height: `${rulerHeight}px` }}
      />
      {leadingHeader}
      {cells.map((cell, idx) => {
        const isMainVideo = cell.kind === 'video' && cell.trackIndex === 0
        const label = cell.labelKey
          ? t(`trackHeaderColumn.${cell.labelKey}`)
          : isMainVideo
          ? t('trackHeaderColumn.mainTrack')
          : cell.kind === 'video'
            ? t('trackHeaderColumn.videoTrack', { index: cell.trackIndex + 1 })
            : t('trackHeaderColumn.audioTrack', { index: cell.trackIndex + 1 })
        const mutedSet = cell.kind === 'video' ? mutedVideoTracks : mutedAudioTracks
        const isMuted = mutedSet?.has(cell.trackIndex) ?? false
        const showRemove = cell.removable ?? !isMainVideo
        const showMute = cell.muteable ?? true
        const handleRemoveClick = () => {
          if (locked || !onRemoveTrack) return
          if (cell.clipCount > 0) {
            const ok = window.confirm(
              t('trackHeaderColumn.removeTrackConfirm', { count: cell.clipCount }),
            )
            if (!ok) return
            onRemoveTrack(cell.kind, cell.trackIndex, true)
          } else {
            onRemoveTrack(cell.kind, cell.trackIndex, false)
          }
        }
        return (
          <div
            key={`${cell.kind}-${cell.testIdSuffix ?? cell.trackIndex}`}
            className="flex shrink-0 flex-col justify-center gap-1 border-b border-border-subtle px-2 py-1"
            style={{ height: `${cellHeights[idx] ?? 60}px` }}
            data-testid={`timeline-${cell.kind}-track-header-${cell.testIdSuffix ?? cell.trackIndex}`}
          >
            <span className="truncate text-xs font-medium text-text-secondary">{label}</span>
            <div
              className="flex items-center gap-1"
              data-testid="timeline-track-header-controls"
            >
              {showMute && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (locked) return
                    onToggleTrackMute?.(cell.kind, cell.trackIndex)
                  }}
                  disabled={locked}
                  className={`rounded border p-1 transition-colors hover:bg-surface-2 ${
                    isMuted
                      ? 'border-warning/50 bg-warning/10 text-warning'
                      : 'border-transparent text-text-muted'
                  } ${locked ? 'cursor-not-allowed opacity-50' : ''}`}
                  aria-pressed={isMuted}
                  aria-label={isMuted ? t('trackHeaderColumn.unmuteLabel', { label }) : t('trackHeaderColumn.muteLabel', { label })}
                  title={isMuted ? t('trackHeaderColumn.unmuteTrack') : t('trackHeaderColumn.muteTrack')}
                  data-testid={`timeline-${cell.kind}-track-mute-${cell.testIdSuffix ?? cell.trackIndex}`}
                  data-muted={isMuted ? 'true' : 'false'}
                >
                  {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                </button>
              )}
              {showRemove && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveClick()
                  }}
                  disabled={locked}
                  className={`rounded p-1 text-text-muted transition-colors hover:bg-surface-2 hover:text-warning ${
                    locked ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  aria-label={t('trackHeaderColumn.removeLabel', { label })}
                  title={t('trackHeaderColumn.removeLabel', { label })}
                  data-testid={`timeline-${cell.kind}-track-remove-${cell.testIdSuffix ?? cell.trackIndex}`}
                  data-clip-count={cell.clipCount}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        )
      })}
      {/* RFC-0633 (Issue 3): 4px-wide vertical splitter at right edge. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('trackHeaderColumn.resizeTrackHeader')}
        className={`absolute top-0 bottom-0 z-10 ${locked ? 'cursor-not-allowed' : 'cursor-col-resize'}`}
        style={{
          right: 0,
          width: '4px',
          touchAction: 'none',
          background: 'transparent',
        }}
        onPointerDown={onSplitterPointerDown}
        onPointerMove={onSplitterPointerMove}
        onPointerUp={onSplitterPointerUp}
        onPointerCancel={onSplitterPointerUp}
        data-testid="timeline-track-header-splitter"
      />
    </div>
  )
}

export type { TrackCell }
